import React, { useState, useMemo, useEffect } from 'react';
import config from '../config';
import api from '../api';
import { formatMoney } from '../utils/format';

// Compute a public price label from tiers / flat ticket price, in the event currency.
const priceLabel = (event) => {
  const currency = event.currency || 'KES';
  const tiers = (event.ticket_tiers || []).filter((t) => t.is_active !== false);
  if (tiers.length > 0) {
    const prices = tiers.map((t) => Number(t.price || 0));
    const min = Math.min(...prices);
    return min > 0 ? `From ${formatMoney(min, currency)}` : 'Free';
  }
  if (event.is_paid_event && Number(event.ticket_price) > 0) {
    return formatMoney(event.ticket_price, currency);
  }
  return 'Free';
};

const EventGrid = ({
  events,
  invitations,
  user,
  visibleEvents,
  onEventClick,
  searchQuery,
  setSearchQuery,
  onShowMore
}) => {
  // Enhanced state management
  const [internalVisible, setInternalVisible] = useState(9);
  const limit = visibleEvents || internalVisible;
  const handleShowMore = onShowMore || (() => setInternalVisible((v) => v + 9));
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'title', 'category'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');   // all | today | weekend | month
  const [priceFilter, setPriceFilter] = useState('all');  // all | free | paid
  const [nearMe, setNearMe] = useState(false);
  const [userLoc, setUserLoc] = useState(null);
  const [savedOnly, setSavedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [bookmarkedEvents, setBookmarkedEvents] = useState(new Set());
  const [hoveredEvent, setHoveredEvent] = useState(null);

  // Load the user's saved-event ids so hearts render filled.
  useEffect(() => {
    if (!user) { setBookmarkedEvents(new Set()); return; }
    let alive = true;
    api.get('/events/saved/ids/')
      .then((r) => { if (alive && Array.isArray(r.data)) setBookmarkedEvents(new Set(r.data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const chipCls = (active) =>
    `px-3 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap transition-colors ${
      active
        ? 'bg-indigo-600 text-white border-indigo-600'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

  // Freeform locations: treat the segment after the last comma as the "city".
  const cityOf = (e) => {
    const loc = (e.location || '').trim();
    return loc.includes(',') ? loc.split(',').pop().trim() : loc;
  };
  const isFreeEvent = (e) => {
    const tiers = (e.ticket_tiers || []).filter((t) => t.is_active !== false);
    if (tiers.length > 0) return Math.min(...tiers.map((t) => Number(t.price || 0))) <= 0;
    return !(e.is_paid_event && Number(e.ticket_price) > 0);
  };
  const matchesDate = (e) => {
    if (dateFilter === 'all') return true;
    if (!e.date) return false;
    const d = new Date(e.date);
    const now = new Date();
    const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
    const diffDays = Math.floor((startOfDay(d) - startOfDay(now)) / 86400000);
    if (dateFilter === 'today') return diffDays === 0;
    if (dateFilter === 'month')
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && diffDays >= 0;
    if (dateFilter === 'weekend') {
      const day = d.getDay(); // 0 Sun, 6 Sat
      return (day === 0 || day === 6) && diffDays >= 0 && diffDays <= 7;
    }
    return true;
  };
  const distanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };
  const enableNearMe = () => {
    if (nearMe) { setNearMe(false); return; }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setNearMe(true); },
      () => setNearMe(false)
    );
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    if (!Array.isArray(events)) {
      return [];
    }

    const cats = [...new Set(events.map(event => event.category).filter(Boolean))];
    return cats.map(cat => {
      const originalCat = cat;
      const formattedCat = cat.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase());
      return {
        value: originalCat,
        label: formattedCat
      };
    });
  }, [events]);

  // Unique cities for the city filter.
  const cities = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const set = new Set();
    events.forEach((e) => { const c = cityOf(e); if (c) set.add(c); });
    return [...set].sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Enhanced filtering and sorting
  const filteredAndSortedEvents = useMemo(() => {
    if (!Array.isArray(events)) {
      return [];
    }

    let filtered = events.filter((event) => {
      const searchMatch =
        (event.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (event.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (event.location || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (Array.isArray(event.keywords) ? event.keywords : []).some((keyword) =>
          keyword.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const categoryMatch = selectedCategory === 'all' || event.category === selectedCategory;
      const cityMatch = selectedCity === 'all' || cityOf(event) === selectedCity;
      const dateMatch = matchesDate(event);
      const priceMatch =
        priceFilter === 'all' ||
        (priceFilter === 'free' ? isFreeEvent(event) : !isFreeEvent(event));

      const savedMatch = !savedOnly || bookmarkedEvents.has(event.id);
      const isPublic = event.visibility === "public";
      const passesFilters = searchMatch && categoryMatch && cityMatch && dateMatch && priceMatch && savedMatch;
      if (!user) return passesFilters && isPublic;

      const isCreator = user?.id === event.created_by;
      const isInvited = invitations.some(inv => inv.event === event.id && inv.recipient === user.id);
      const canSee = isPublic || isCreator || isInvited;

      return passesFilters && canSee;
    });

    // "Near me" wins the sort: nearest first (needs coordinates + the user's location).
    if (nearMe && userLoc) {
      filtered = filtered
        .filter((e) => e.latitude != null && e.longitude != null)
        .sort(
          (a, b) =>
            distanceKm(userLoc.lat, userLoc.lng, a.latitude, a.longitude) -
            distanceKm(userLoc.lat, userLoc.lng, b.latitude, b.longitude)
        );
    } else {
      filtered.sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case 'title':
            comparison = (a.title || '').localeCompare(b.title || '');
            break;
          case 'category':
            comparison = (a.category || '').localeCompare(b.category || '');
            break;
          case 'date':
          default:
            comparison = new Date(a.date) - new Date(b.date);
            break;
        }
        return sortOrder === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, searchQuery, selectedCategory, selectedCity, dateFilter, priceFilter, nearMe, userLoc, savedOnly, bookmarkedEvents, sortBy, sortOrder, user, invitations]);

  // Helper functions
  const getEventStatus = (event) => {
    const now = new Date();
    const eventDate = new Date(event.date);
    const eventEndDate = event.end_date ? new Date(event.end_date) : eventDate;

    if (eventDate > now) return 'upcoming';
    if (eventEndDate < now) return 'past';
    return 'ongoing';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ongoing': return 'bg-green-100 text-green-800 border-green-200';
      case 'past': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const toggleBookmark = (eventId, e) => {
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    const has = bookmarkedEvents.has(eventId);
    // Optimistic update, then persist.
    const next = new Set(bookmarkedEvents);
    has ? next.delete(eventId) : next.add(eventId);
    setBookmarkedEvents(next);
    const req = has ? api.delete(`/events/${eventId}/save/`) : api.post(`/events/${eventId}/save/`);
    req.catch(() => {
      // Roll back on failure.
      const revert = new Set(bookmarkedEvents);
      has ? revert.add(eventId) : revert.delete(eventId);
      setBookmarkedEvents(revert);
    });
  };

  const shareEvent = (event, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/events/${event.slug}`;
    if (navigator.share) {
      navigator.share({ title: event.title, text: event.description, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const shareWhatsApp = (event, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/events/${event.slug}`;
    const text = `${event.title} — ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Render event card
  const renderEventCard = (event) => {
    const isCreator = user?.id === event.created_by;
    const isInvited = invitations.some(inv => inv.event === event.id && inv.recipient === user?.id);
    const status = getEventStatus(event);
    const isBookmarked = bookmarkedEvents.has(event.id);

    return (
      <div
        key={event.slug}
        className={`group bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${viewMode === 'list' ? 'flex' : 'flex flex-col'
          }`}
        onClick={() => onEventClick(event.slug)}
        onMouseEnter={() => setHoveredEvent(event.id)}
        onMouseLeave={() => setHoveredEvent(null)}
      >
        {/* Image */}
        <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-44 flex-shrink-0' : 'w-full h-48'}`}>
          {event.thumbnail_photo ? (
            <img
              src={event.thumbnail_photo.startsWith('http') ? event.thumbnail_photo : `${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
              <svg className="w-12 h-12 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* gradient for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent pointer-events-none" />

          {/* Date badge */}
          {event.date && (
            <div className="absolute top-3 left-3 bg-white/95 dark:bg-gray-900/90 rounded-xl px-2.5 py-1.5 text-center shadow-md leading-none">
              <div className="text-lg font-extrabold text-gray-900 dark:text-white">
                {new Date(event.date).getDate()}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
                {new Date(event.date).toLocaleString('en-US', { month: 'short' })}
              </div>
            </div>
          )}

          {/* Price pill */}
          <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-white/95 dark:bg-gray-900/85 text-gray-900 dark:text-white text-sm font-bold shadow-md">
            {priceLabel(event)}
          </span>

          {/* Status pill */}
          <span className={`absolute bottom-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusColor(status)}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>

          {/* Bookmark Button */}
          <button
            onClick={(e) => toggleBookmark(event.id, e)}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${isBookmarked
              ? 'bg-red-500 text-white'
              : 'bg-white/85 dark:bg-gray-900/70 text-gray-600 dark:text-gray-300 hover:bg-white hover:text-red-500'
              }`}
          >
            <svg className="w-4 h-4" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {event.title}
              </h3>

              {event.category && (
                <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-sm rounded-full mb-3 capitalize">
                  {event.category_display || event.category.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            {/* Quick Actions */}
            <div className={`flex gap-2 transition-opacity duration-200 ${hoveredEvent === event.id ? 'opacity-100' : 'opacity-0'
              }`}>
              <button
                onClick={(e) => shareWhatsApp(event, e)}
                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Share on WhatsApp"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.582 0 11.941-5.359 11.944-11.893a11.821 11.821 0 00-3.487-8.401" />
                </svg>
              </button>
              <button
                onClick={(e) => shareEvent(event, e)}
                className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                title="Share event"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
              {event.description}
            </p>
          )}

          {/* Event Details */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(event.date)} {event.time && `• ${event.time}`}
            </div>

            {event.location && (
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {event.location}
              </div>
            )}
          </div>

          {/* Keywords/Tags */}
          {Array.isArray(event.keywords) && event.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {event.keywords.slice(0, 3).map((keyword, index) => (
                <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md">
                  #{keyword}
                </span>
              ))}
              {event.keywords.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md">
                  +{event.keywords.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* User Status */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {isCreator && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Creator
                </span>
              )}

              {isInvited && !isCreator && (
                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Invited
                </span>
              )}
            </div>

            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              View details
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="py-8 px-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Discover Events
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {filteredAndSortedEvents.length} events found
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Search events by title, description, location, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-6 py-4 pl-12 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
          />
          <svg className="absolute left-4 top-4 w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Quick filter chips — city, near me, date, price, genres */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          <button type="button" onClick={enableNearMe} className={chipCls(nearMe)}>
            📍 Near me
          </button>
          <button
            type="button"
            onClick={() => setDateFilter(dateFilter === 'today' ? 'all' : 'today')}
            className={chipCls(dateFilter === 'today')}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setDateFilter(dateFilter === 'weekend' ? 'all' : 'weekend')}
            className={chipCls(dateFilter === 'weekend')}
          >
            This weekend
          </button>
          <button
            type="button"
            onClick={() => setPriceFilter(priceFilter === 'free' ? 'all' : 'free')}
            className={chipCls(priceFilter === 'free')}
          >
            Free
          </button>
          {user && (
            <button type="button" onClick={() => setSavedOnly((s) => !s)} className={chipCls(savedOnly)}>
              ♥ Saved
            </button>
          )}
          <span className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0" />
          <button type="button" onClick={() => setSelectedCategory('all')} className={chipCls(selectedCategory === 'all')}>
            All genres
          </button>
          {categories.slice(0, 10).map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setSelectedCategory(selectedCategory === cat.value ? 'all' : cat.value)}
              className={chipCls(selectedCategory === cat.value)}
            >
              {cat.label}
            </button>
          ))}
          {cities.length > 0 && (
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="ml-1 flex-shrink-0 px-3 py-1.5 rounded-full text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              <option value="all">All cities</option>
              {cities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="date">Date</option>
                  <option value="title">Title</option>
                  <option value="category">Category</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Order
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Events Grid/List */}
      <div className={`${viewMode === 'grid'
        ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8'
        : 'space-y-6'
        }`}>
        {filteredAndSortedEvents.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No events found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || selectedCategory !== 'all'
                ? "Try adjusting your search or filters"
                : "No events are available at the moment"}
            </p>
          </div>
        ) : (
          filteredAndSortedEvents.slice(0, limit).map(renderEventCard)
        )}
      </div>

      {/* Show More Button */}
      {filteredAndSortedEvents.length > limit && (
        <div className="text-center mt-12">
          <button
            onClick={handleShowMore}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-2xl transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Show More Events ({filteredAndSortedEvents.length - limit} remaining)
          </button>
        </div>
      )}
    </div>
  );
};

export default EventGrid;